import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, UtensilsCrossed, Beer, Ticket, BedDouble, ShoppingBag, Landmark, Heart, Wrench } from "lucide-react";
import CategoryActions from "@/components/yts/categorynav/CategoryActions";
import CategoryQuickFilters from "@/components/yts/categorynav/CategoryQuickFilters";
import { useExplore } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";

/** One recognizable icon per category; the secondary filter row remains uncluttered. */
const BROWSE = [
  { category: "Explore", en: "All", es: "Todo", icon: Compass },
  { category: "Gastronomy", en: "Eat", es: "Comer", icon: UtensilsCrossed },
  { category: "Nightlife", en: "Drink", es: "Beber", icon: Beer },
  { category: "Experiences", en: "Do", es: "Hacer", icon: Ticket },
  { category: "Stays", en: "Stays", es: "Hospedajes", icon: BedDouble },
  { category: "Shop", en: "Shop", es: "Comprar", icon: ShoppingBag },
  { category: "Culture", en: "Culture", es: "Cultura", icon: Landmark },
  { category: "LGBTQ+", en: "LGBTQ+", es: "LGBTQ+", icon: Heart },
  { category: "Services", en: "Services", es: "Servicios", icon: Wrench },
];

export default function CategoryNav() {
  const { lang, t } = useLang();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    activeChip, setActiveChip, setExperienceBucket, setFilterSubs, setFilterOpen,
    hasFilter, clearFilters, nearError
  } = useExplore();
  const chipRefs = useRef({});
  const previousCategory = useRef(activeChip);

  useEffect(() => {
    chipRefs.current[activeChip]?.scrollIntoView({
      behavior: "smooth", inline: "center", block: "nearest"
    });
  }, [activeChip]);

  useEffect(() => {
    const previous = previousCategory.current;
    previousCategory.current = activeChip;
    if (!previous || previous === activeChip || activeChip === "Explore") return;
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        const section = document.getElementById("category-results");
        const target = section?.querySelector("h1, h2") || section;
        if (!target) return;
        const bottom = document.querySelector(".yts-category-nav")?.getBoundingClientRect().bottom || 0;
        const delta = target.getBoundingClientRect().top - bottom - 18;
        if (Math.abs(delta) >= 2) {
          const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
          window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: reduceMotion ? "auto" : "smooth" });
        }
      });
    });
    return () => {
      window.cancelAnimationFrame(outerFrame);
      if (innerFrame) window.cancelAnimationFrame(innerFrame);
    };
  }, [activeChip]);

  const choose = (category) => {
    if (location.pathname !== "/" || location.search) {
      navigate({ pathname: "/", search: "" }, { replace: true, preventScrollReset: true });
    }
    clearFilters?.();
    setFilterSubs?.(new Set());
    setExperienceBucket?.("All");
    setActiveChip?.(category);
    if (category === "Explore") {
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    }
  };

  const actionProps = {
    isDark,
    lang,
    hasFilter,
    onOpenFilters: () => setFilterOpen?.(true),
    onNearMe: () => navigate("/map?near=1"),
    onClear: () => {
      clearFilters?.();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  };

  const baseTab = "inline-flex h-[47px] shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap border-b-[2px] px-3 text-[11.5px] font-semibold tracking-[.005em] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E30613] focus-visible:ring-offset-[-2px] sm:px-4 sm:text-[13px]";
  const activeTab = isDark
    ? "border-white text-white font-extrabold"
    : "border-[#171717] text-[#171717] font-extrabold";
  const inactiveTab = isDark
    ? "border-transparent text-white/65 hover:text-white"
    : "border-transparent text-[#686762] hover:text-[#171717]";

  return (
    <nav
      data-tour="categories"
      className={`yts-category-nav sticky top-[60px] z-[49] w-full border-b sm:top-[64px] ${isDark ? "border-white/10 bg-[#171719] text-white" : "border-black/[0.10] bg-white text-[#171717]"}`}
      aria-label={lang === "es" ? "Explorar por categoría" : "Browse by category"}
    >
      <div className="mx-auto flex max-w-[1240px] items-center gap-1.5 pl-3 pr-2 sm:gap-3 sm:px-6 lg:px-8">
        <div className="no-scrollbar flex min-w-0 flex-1 snap-x snap-proximity items-center gap-0.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] sm:gap-1">
          {BROWSE.map((entry) => {
            const active = !entry.to && activeChip === entry.category;
            const label = lang === "es" ? entry.es : entry.en;
            const Icon = entry.icon;
            const contents = <><Icon aria-hidden="true" className="h-[15px] w-[15px] shrink-0 sm:h-4 sm:w-4" strokeWidth={1.85} /><span>{label}</span></>;
            const className = `${baseTab} ${active ? activeTab : inactiveTab}`;
            if (entry.to) {
              return <Link key={entry.category} to={entry.to} className={className}>{contents}</Link>;
            }
            return (
              <button
                key={entry.category}
                type="button"
                ref={(node) => { chipRefs.current[entry.category] = node; }}
                onClick={() => choose(entry.category)}
                aria-pressed={active}
                className={className}
              >
                {contents}
              </button>
            );
          })}
        </div>
        <div className={`flex shrink-0 items-center gap-0.5 border-l pl-1.5 sm:pl-2 ${isDark ? "border-white/15" : "border-black/10"}`}>
          <CategoryActions compact className="flex items-center gap-0.5 sm:hidden" {...actionProps} />
          <CategoryActions className="hidden items-center gap-1 sm:flex" {...actionProps} />
        </div>
      </div>
      {activeChip !== "Explore" && activeChip !== "Stays" && <CategoryQuickFilters category={activeChip} />}
      {nearError && (
        <p role="status" aria-live="polite" className="mx-auto max-w-[1240px] px-3 pb-1 text-right text-[10px]">
          {t(nearError === "unavailable" ? "nearMe.unavailable" : "nearMe.error")}
        </p>
      )}
    </nav>
  );
}
